import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  UrlSegment,
} from '@angular/router';
import { DOCUMENT, Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import {
  combineLatest,
  debounceTime,
  filter,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';

import { Config } from '../../core/configs/config';
import { ERoutes } from '../../core/enums/routes.enum';
import { ESvgIcons } from '../../core/enums/svg-icons';
import { EInfoType } from '../../core/enums/country-info-type.enum';
import { CityDetails } from '../../core/types/models/city-details.model';
import { CityResponse } from '../../core/types/models/city-response.model';
import { RegionDetails } from '../../core/types/models/region-details.model';
import { VoiceMapResponse } from '../../core/types/models/voice-map-response';
import { RegionResponse } from '../../core/types/models/region-response.model';
import { CountryDetails } from '../../core/types/models/country-details.model';
import { CountryResponse } from '../../core/types/models/country-response.model';
import { OccasionGeniusResponse } from '../../core/types/models/occasion-genius-response.model';

import { BreakpointService } from '../../core/services/breakpoint.service';
import { CloudinaryService } from '../../core/services/cloudinary.service';
import { CountryInfoService } from '../../core/services/country-info.service';
import { CountryInfoTextService } from './services/country-info-text.service';
import { CountryInfoParseService } from './services/country-info-parse.service';
import { MetaTagsConfigService } from '../../core/services/meta-tags-config.service';

import { IViatorCategories } from 'src/app/shared/used-components/viator/viator.component';

@Component({
  selector: 'app-country-info',
  templateUrl: './country-info.component.html',
  styleUrls: ['./country-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CountryInfoComponent implements OnInit, OnDestroy {
  readonly eSvgIcons = ESvgIcons;
  readonly ERoutes = ERoutes;
  readonly eInfoType = EInfoType;

  bannerImgUrl: string = `${Config.imgCdnUrl}images/country-info_banner.png`;
  breadcrumbs: { text: string; path: string }[] = [];
  menuOpened: boolean[] = [false, false, false];

  isDefaultView: boolean = true;
  countryResponse: CountryResponse;
  countryDetails: CountryDetails;
  regionResponse: RegionResponse;
  regionDetails: RegionDetails;
  cityResponse: CityResponse;
  cityDetails: CityDetails;
  categoriesResponse: IViatorCategories = {};
  occasionGeniusResponse: OccasionGeniusResponse = new OccasionGeniusResponse(
    {},
  );
  voiceMapResponse: VoiceMapResponse;
  isFetching: boolean;

  private destroySubject$ = new Subject<void>();
  private resizeSubject = new Subject<void>();
  private urlChangeSubscription: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public router: Router,
    public route: ActivatedRoute,
    public metaTagsConfigService: MetaTagsConfigService,
    public cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    public countryInfoParseService: CountryInfoParseService,
    public countryInfoTextService: CountryInfoTextService,
    public countryInfoService: CountryInfoService,
    public location: Location,
    protected readonly breakpointService: BreakpointService,
    protected readonly cloudinaryService: CloudinaryService,
  ) {
    this.checkIfDefaultView(this.route.snapshot.url);
    this.bannerImgUrl = this.cloudinaryService.getCloudImage(
      this.bannerImgUrl,
      true,
    );
  }

  get isOnlyCountry(): boolean {
    return (
      !!this.route.snapshot.params['country'] &&
      !this.route.snapshot.params['region'] &&
      !this.route.snapshot.params['city']
    );
  }

  get isOnlyRegion(): boolean {
    return (
      !!this.route.snapshot.params['country'] &&
      !!this.route.snapshot.params['region'] &&
      !this.route.snapshot.params['city']
    );
  }

  get isOnlyCity(): boolean {
    return (
      !!this.route.snapshot.params['country'] &&
      !!this.route.snapshot.params['region'] &&
      !!this.route.snapshot.params['city']
    );
  }

  get defaultLocationName(): string {
    if (this.isOnlyCountry)
      return this.route.snapshot.params['country']?.toUpperCase();
    if (this.isOnlyRegion)
      return this.route.snapshot.params['region']?.toUpperCase();
    if (this.isOnlyCity)
      return this.route.snapshot.params['city']?.toUpperCase();
    return '';
  }

  public get country(): string {
    return this.route.snapshot.params?.country || '';
  }

  @HostListener('window:resize', ['$event']) onResize() {
    this.resizeSubject.next();
  }

  ngOnInit(): void {
    this.resizeSubject
      .pipe(takeUntil(this.destroySubject$), debounceTime(300))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.getCountryInfoBySnapshotUrlSegment(this.route.snapshot.url);
    this.checkUrlChanges();

    combineLatest([
      this.countryInfoParseService.countryResponse$,
      this.countryInfoParseService.regionResponse$,
      this.countryInfoParseService.cityResponse$,
      this.countryInfoParseService.isFetching$,
      this.countryInfoParseService.categoriesResponse$,
      this.countryInfoParseService.voiceMapResponse$,
      this.countryInfoParseService.occasionGeniusResponse$,
    ])
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        ([
          countryResponse,
          regionResponse,
          cityResponse,
          isFetching,
          categoriesResponse,
          voiceMapResponse,
          occasionGeniusResponse,
        ]) => {
          this.countryResponse = countryResponse;
          this.regionResponse = regionResponse;
          this.cityResponse = cityResponse;
          this.isFetching = isFetching;
          this.countryDetails =
            this.countryResponse.countryInfo.getFirstCountry();
          this.regionDetails = this.regionResponse.regionDetails;
          this.cityDetails = this.cityResponse.cityInfo.getFirstCity();
          this.categoriesResponse = categoriesResponse;
          this.occasionGeniusResponse = occasionGeniusResponse;
          this.voiceMapResponse = voiceMapResponse;
          this.cdr.markForCheck();
        },
      );
  }

  ngOnDestroy(): void {
    if (this.urlChangeSubscription) {
      this.urlChangeSubscription.unsubscribe();
    }
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  getCountryInfoBySnapshotUrlSegment(segments: UrlSegment[]): void {
    this.breadcrumbs = [];
    let pathUrl = '';

    segments.forEach((segment) => {
      pathUrl += `/${segment.path}`;
      const breadcrumb = { text: segment.path, path: pathUrl };
      this.breadcrumbs.push(breadcrumb);
    });

    this.countryInfoParseService.fetchData(segments);
  }

  public planAPerfectTrip(): void {
    const { countryInfo, topDest } = this.countryResponse;
    const currentCountry = countryInfo.getFirstCountry();
    switch (true) {
      case this.isOnlyCountry:
        this.countryInfoService.setPerfectTripState(
          topDest,
          currentCountry.place_id,
          currentCountry.name,
        );
        break;
      case this.isOnlyRegion:
        const regionName = this.regionDetails.name;
        const regionId = this.regionDetails.id;
        this.countryInfoService.setPerfectTripState(
          regionId,
          currentCountry.place_id,
          regionName,
        );
        break;
      case this.isOnlyCity:
        const cityName = this.cityDetails.name;
        const cityId = this.cityDetails.place_id;
        this.countryInfoService.setPerfectTripState(
          cityId,
          currentCountry.place_id,
          cityName,
        );
        break;
    }

    this.router.navigate([ERoutes.SLASH, ERoutes.TRIP_PLANNER]).then();
  }

  private checkUrlChanges(): void {
    this.urlChangeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event) {
          const urlBuffer: Array<string> = event.urlAfterRedirects
            .split('/')
            .splice(1);
          if (urlBuffer.length === 1) {
            this.breadcrumbs = [];
          }
          this.checkIfDefaultView(this.route.snapshot.url);
          this.getCountryInfoBySnapshotUrlSegment(this.route.snapshot.url);
        }
      });
  }

  private checkIfDefaultView(segments: UrlSegment[]): void {
    this.isDefaultView = !(
      segments.length === 1 && segments[0].path.startsWith('things-to-do-in-')
    );
  }
}
